import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Center,
  Group,
  Indicator,
  NativeSelect,
  Pagination,
  Popover,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconAlertTriangle, IconDots, IconX } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useCases, CASES_REFRESH_MS, type ListCasesFilters } from '../api/cases';
import { useOrgs } from '../api/orgs';
import { AutoRefreshIndicator } from '../components/auto-refresh-indicator';
import { OpenedFilter } from '../components/opened-filter';
import { SearchLoading } from '../components/search-loading';

const PAGE_SIZE = 25;

const STATUS_COLOURS: Record<string, string> = {
  open: 'blue',
  awaiting_histology: 'yellow',
  completed: 'green',
};

function statusBadge(status: string) {
  return (
    <Badge color={STATUS_COLOURS[status] ?? 'gray'} variant="light">
      {status}
    </Badge>
  );
}

function shortId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

// Convert "YYYY-MM-DD" to start-of-day ISO for opened_after, end-of-day ISO for opened_before.
function dateToBoundaryIso(date: string, mode: 'start' | 'end'): string | undefined {
  if (!date) return undefined;
  const suffix = mode === 'start' ? 'T00:00:00.000Z' : 'T23:59:59.999Z';
  return `${date}${suffix}`;
}

export function CasesListPage() {
  const orgs = useOrgs();
  const [params, setParams] = useSearchParams();

  // Filter state lives in the URL so the page is deep-linkable and
  // shareable. Each filter has both the raw URL param and a derived
  // value used to talk to the API.
  const orgId = params.get('org_id') ?? '';
  const status = params.get('status') ?? '';
  const patientIdParam = params.get('patient_id') ?? '';
  const searchParam = params.get('search') ?? '';
  const openedAfter = params.get('opened_after') ?? '';
  const openedBefore = params.get('opened_before') ?? '';
  const page = Math.max(1, Number(params.get('page') ?? '1'));

  // Patient id is free-text — debounce so each keystroke doesn't fire
  // a URL update / refetch.
  const [patientInput, setPatientInput] = useState(patientIdParam);
  const [debouncedPatient] = useDebouncedValue(patientInput, 300);

  useEffect(() => {
    if (debouncedPatient === patientIdParam) return;
    const next = new URLSearchParams(params);
    if (debouncedPatient) next.set('patient_id', debouncedPatient);
    else next.delete('patient_id');
    next.set('page', '1');
    setParams(next, { replace: true });
  }, [debouncedPatient]);

  // Search is free-text (org or patient name) — debounce like patient id.
  const [searchInput, setSearchInput] = useState(searchParam);
  const [debouncedSearch] = useDebouncedValue(searchInput, 300);

  useEffect(() => {
    if (debouncedSearch === searchParam) return;
    const next = new URLSearchParams(params);
    if (debouncedSearch) next.set('search', debouncedSearch);
    else next.delete('search');
    next.set('page', '1');
    setParams(next, { replace: true });
  }, [debouncedSearch]);

  function patchParams(patch: Partial<Record<string, string>>, resetPage = false) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === '') next.delete(k);
      else next.set(k, v);
    }
    if (resetPage) next.set('page', '1');
    setParams(next, { replace: true });
  }

  const hasActiveFilters = Boolean(
    orgId || status || searchParam || openedAfter || openedBefore || patientIdParam,
  );

  function clearAllFilters() {
    // `replace: true` to match every other param write in this file (patchParams,
    // the debounced sync effects) — clearing filters shouldn't push a history entry.
    setParams(new URLSearchParams(), { replace: true });
    setSearchInput('');
    setPatientInput('');
  }

  const filters: ListCasesFilters = {
    orgId: orgId || undefined,
    status: (status || undefined) as ListCasesFilters['status'],
    patientId: debouncedPatient || undefined,
    search: debouncedSearch || undefined,
    openedAfter: dateToBoundaryIso(openedAfter, 'start'),
    openedBefore: dateToBoundaryIso(openedBefore, 'end'),
    page,
    limit: PAGE_SIZE,
  };
  const query = useCases(filters);

  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Cases</Title>
        <AutoRefreshIndicator
          lastFetchedAt={query.dataUpdatedAt}
          isFetching={query.isFetching}
          intervalMs={CASES_REFRESH_MS}
        />
      </Group>

      <Group gap="md" align="flex-end" wrap="wrap">
        <TextInput
          label="Search"
          placeholder="Org or patient name"
          value={searchInput}
          onChange={(e) => setSearchInput(e.currentTarget.value)}
          style={{ flex: 1, minWidth: 220 }}
        />
        <NativeSelect
          label="Organisation"
          value={orgId}
          onChange={(e) => patchParams({ org_id: e.currentTarget.value }, true)}
          data={[
            { value: '', label: 'All orgs' },
            ...(orgs.data ?? []).map((o) => ({ value: o.id, label: o.name })),
          ]}
          w={180}
        />
        <NativeSelect
          label="Status"
          value={status}
          onChange={(e) => patchParams({ status: e.currentTarget.value }, true)}
          data={[
            { value: '', label: 'Any' },
            { value: 'open', label: 'Open' },
            { value: 'awaiting_histology', label: 'Awaiting histology' },
            { value: 'completed', label: 'Completed' },
          ]}
          w={160}
        />
        <OpenedFilter
          after={openedAfter}
          before={openedBefore}
          onChange={(a, b) => patchParams({ opened_after: a, opened_before: b }, true)}
        />
        <Popover position="bottom-end" withinPortal shadow="md">
          <Popover.Target>
            <Indicator disabled={!patientIdParam} size={8} offset={4} color="blue">
              <ActionIcon variant="default" size={36} aria-label="More filters">
                <IconDots size={18} />
              </ActionIcon>
            </Indicator>
          </Popover.Target>
          <Popover.Dropdown>
            <TextInput
              label="Patient ID (exact)"
              placeholder="UUID"
              value={patientInput}
              onChange={(e) => setPatientInput(e.currentTarget.value)}
              w={260}
            />
          </Popover.Dropdown>
        </Popover>
        {hasActiveFilters && (
          <Button
            variant="subtle"
            color="gray"
            leftSection={<IconX size={16} />}
            onClick={clearAllFilters}
          >
            Clear filters
          </Button>
        )}
      </Group>

      {query.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          Could not load cases.
        </Alert>
      )}

      {query.isPending ? (
        <SearchLoading />
      ) : (query.data?.items ?? []).length === 0 ? (
        <Center mih={200}>
          <Text c="dimmed">No cases match these filters.</Text>
        </Center>
      ) : (
        <>
          <Table highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Case</Table.Th>
                <Table.Th>Org</Table.Th>
                <Table.Th>Patient</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th ta="right">Dermoscopic</Table.Th>
                <Table.Th ta="right">Macroscopic</Table.Th>
                <Table.Th>Opened</Table.Th>
                <Table.Th>Updated</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(query.data?.items ?? []).map((c) => (
                <Table.Tr key={c.id}>
                  <Table.Td>
                    <Anchor component={Link} to={`/cases/${c.id}`} size="sm">
                      {shortId(c.id)}
                    </Anchor>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{c.org_name}</Text>
                    <Text size="xs" c="dimmed">
                      {c.org_region}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {c.patient_given_name} {c.patient_family_name}
                  </Table.Td>
                  <Table.Td>{statusBadge(c.status)}</Table.Td>
                  <Table.Td ta="right">{c.dermoscopic_image_count}</Table.Td>
                  <Table.Td ta="right">{c.macroscopic_image_count}</Table.Td>
                  <Table.Td>
                    <Text size="sm" title={c.opened_at}>
                      {formatDate(c.opened_at)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" title={c.updated_at}>
                      {formatDate(c.updated_at)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          <Group justify="space-between">
            <Text c="dimmed" size="sm">
              {total} total
            </Text>
            <Pagination
              value={page}
              total={totalPages}
              onChange={(p) => patchParams({ page: String(p) })}
              size="sm"
            />
          </Group>
        </>
      )}
    </Stack>
  );
}
